import { DEFAULT_BRAND_THEME } from './defaults'
import { buildBrandPalette, normalizeCssColor } from './colors'
import { getSvgMetrics } from './svgMetrics'
import { CHART_IDS } from '../../charts/chartIds'

const SVG_NS = 'http://www.w3.org/2000/svg'
const LINE_STROKE_WIDTH_CHART_IDS = new Set([
  CHART_IDS.LINE_CHART,
  CHART_IDS.PARALLEL_COORDINATES,
])

function createSvgNode(tagName, attrs = {}) {
  const node = document.createElementNS(SVG_NS, tagName)
  Object.entries(attrs).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      node.setAttribute(key, String(value))
    }
  })
  return node
}

function shouldSkipStyledNode(node) {
  const blocklistPattern =
    /background|grid|legend|axis|label|header|land|country|graticule/i

  let current = node
  while (current && current.nodeType === 1) {
    const id = current.getAttribute('id') || ''
    const dataName = current.getAttribute('data-name') || ''
    const className = current.getAttribute('class') || ''
    if (
      blocklistPattern.test(id) ||
      blocklistPattern.test(dataName) ||
      blocklistPattern.test(className)
    ) {
      return true
    }
    current = current.parentElement
  }

  return false
}

function isAxisNode(node) {
  let current = node
  while (current && current.nodeType === 1) {
    const id = current.getAttribute('id') || ''
    const dataName = current.getAttribute('data-name') || ''
    const className = current.getAttribute('class') || ''
    if (/axis/i.test(id) || /axis/i.test(dataName) || /axis/i.test(className)) {
      return true
    }
    current = current.parentElement
  }
  return false
}

function getInheritedFontSize(node) {
  // Walk ancestors to find the closest explicit font-size, preferring the
  // cached original (data-rawgraphs-theme-base-font-size) so that already-
  // scaled ancestors don't cause tspan children to be double-scaled.
  let current = node.parentElement
  while (current && current.nodeType === 1) {
    const cached = current.dataset?.rawgraphsThemeBaseFontSize
    if (cached) return cached
    const fs = current.getAttribute('font-size') || current.style.fontSize
    if (fs) return fs
    current = current.parentElement
  }
  return null
}

function isLegendNode(node) {
  let current = node
  while (current && current.nodeType === 1) {
    const id = current.getAttribute('id') || ''
    const dataName = current.getAttribute('data-name') || ''
    const className = current.getAttribute('class') || ''
    if (/legend/i.test(id) || /legend/i.test(dataName) || /legend/i.test(className)) {
      return true
    }
    current = current.parentElement
  }
  return false
}

function collectMarkColorMap(rootNode) {
  const colorMap = new Map()
  const markNodes = Array.from(
    rootNode.querySelectorAll('path, rect, circle, polygon, ellipse')
  ).filter((node) => !shouldSkipStyledNode(node))

  markNodes.forEach((node) => {
    ;['fill', 'stroke'].forEach((prop) => {
      const rawValue = node.getAttribute(prop) || node.style[prop]
      const normalized = normalizeCssColor(rawValue)
      if (!normalized) return
      if (normalized === '#ffffff' || normalized === '#000000') return
      if (!colorMap.has(normalized)) {
        colorMap.set(normalized, [])
      }
      colorMap.get(normalized).push({ node, prop })
    })
  })

  return colorMap
}

function applyBrandThemeToMarks(svgNode, theme) {
  const contentRoot =
    svgNode.querySelector('[data-lugsail-theme-content="true"]') || svgNode
  const colorMap = collectMarkColorMap(contentRoot)
  if (!colorMap.size) return

  const palette = buildBrandPalette(theme)
  Array.from(colorMap.entries()).forEach(([originalColor, usages], index) => {
    const nextColor = palette[index % palette.length]
    if (originalColor === nextColor) return

    usages.forEach(({ node, prop }) => {
      node.setAttribute(prop, nextColor)
      node.style[prop] = nextColor
    })
  })
}

function applyBrandThemeOpacityToMarks(svgNode, theme) {
  const contentRoot =
    svgNode.querySelector('[data-lugsail-theme-content="true"]') || svgNode
  const markOpacity = Number(theme?.markOpacity)
  if (!Number.isFinite(markOpacity) || markOpacity >= 0.999) return

  Array.from(
    contentRoot.querySelectorAll('path, rect, circle, polygon, ellipse, line')
  )
    .filter((node) => !shouldSkipStyledNode(node))
    .forEach((node) => {
      const baseOpacityRaw =
        node.dataset.rawgraphsThemeBaseOpacity ||
        node.getAttribute('opacity') ||
        node.style.opacity ||
        '1'
      const baseOpacity = Number(baseOpacityRaw)
      const nextOpacity = Number.isFinite(baseOpacity)
        ? Math.max(0, Math.min(1, baseOpacity * markOpacity))
        : Math.max(0, Math.min(1, markOpacity))

      if (!node.dataset.rawgraphsThemeBaseOpacity) {
        node.dataset.rawgraphsThemeBaseOpacity = String(baseOpacityRaw)
      }
      node.setAttribute('opacity', String(nextOpacity))
      node.style.opacity = String(nextOpacity)
    })
}

function applyBrandThemeToLineStrokeWidth(svgNode, theme, chartId) {
  if (!LINE_STROKE_WIDTH_CHART_IDS.has(chartId)) {
    return
  }

  const lineStrokeScale = Number(theme?.lineStrokeScale ?? 1)
  if (!Number.isFinite(lineStrokeScale) || Math.abs(lineStrokeScale - 1) < 0.001) {
    return
  }

  const contentRoot =
    svgNode.querySelector('[data-lugsail-theme-content="true"]') || svgNode

  Array.from(contentRoot.querySelectorAll('path, line, polyline'))
    .filter((node) => !shouldSkipStyledNode(node))
    .forEach((node) => {
      const rawStrokeWidth =
        node.dataset.rawgraphsThemeBaseStrokeWidth ||
        node.getAttribute('stroke-width') ||
        node.style.strokeWidth ||
        '1'
      const baseStrokeWidth = Number.parseFloat(rawStrokeWidth)
      const nextStrokeWidth = Number.isFinite(baseStrokeWidth)
        ? Math.max(0.25, Math.round(baseStrokeWidth * lineStrokeScale * 100) / 100)
        : Math.max(0.25, Math.round(lineStrokeScale * 100) / 100)

      if (!node.dataset.rawgraphsThemeBaseStrokeWidth) {
        node.dataset.rawgraphsThemeBaseStrokeWidth = String(rawStrokeWidth)
      }

      node.setAttribute('stroke-width', String(nextStrokeWidth))
      node.style.strokeWidth = `${nextStrokeWidth}px`
    })
}

function applyConfiguredColorScaleOpacity(svgNode, colorScaleOption) {
  const contentRoot =
    svgNode.querySelector('[data-lugsail-theme-content="true"]') || svgNode
  const userScaleValues = Array.isArray(colorScaleOption?.userScaleValues)
    ? colorScaleOption.userScaleValues
    : []
  const defaultOpacity = Number.isFinite(Number(colorScaleOption?.defaultOpacity))
    ? Math.max(0, Math.min(1, Number(colorScaleOption.defaultOpacity)))
    : 1

  const opacityByDomain = new Map()
  const opacityByColor = new Map()
  userScaleValues.forEach((item) => {
    const domain = item?.domain
    const color = normalizeCssColor(item?.range)
    const opacity = Number(item?.opacity)
    if (!Number.isFinite(opacity)) return
    if (domain !== undefined && domain !== null) {
      opacityByDomain.set(String(domain), Math.max(0, Math.min(1, opacity)))
    }
    if (!color) return
    opacityByColor.set(color, Math.max(0, Math.min(1, opacity)))
  })

  if (defaultOpacity >= 0.999 && !opacityByColor.size && !opacityByDomain.size) return

  Array.from(
    contentRoot.querySelectorAll('path, rect, circle, polygon, ellipse, line')
  )
    .filter((node) => !shouldSkipStyledNode(node))
    .forEach((node) => {
      const explicitDomain = node.getAttribute('data-rawgraphs-color-domain')
      const fill = normalizeCssColor(node.getAttribute('fill') || node.style.fill)
      const stroke = normalizeCssColor(
        node.getAttribute('stroke') || node.style.stroke
      )
      const configuredOpacity = explicitDomain
        ? opacityByDomain.get(explicitDomain) ?? defaultOpacity
        : opacityByColor.get(fill) ?? opacityByColor.get(stroke) ?? defaultOpacity

      if (!Number.isFinite(configuredOpacity)) return

      const baseOpacityRaw =
        node.dataset.rawgraphsThemeBaseOpacity ||
        node.getAttribute('opacity') ||
        node.style.opacity ||
        '1'
      const baseOpacity = Number(baseOpacityRaw)
      const nextOpacity = Number.isFinite(baseOpacity)
        ? Math.max(0, Math.min(1, baseOpacity * configuredOpacity))
        : configuredOpacity

      if (!node.dataset.rawgraphsThemeBaseOpacity) {
        node.dataset.rawgraphsThemeBaseOpacity = String(baseOpacityRaw)
      }
      node.setAttribute('opacity', String(nextOpacity))
      node.style.opacity = String(nextOpacity)
    })
}

function applyBrandThemeToLabels(svgNode, theme) {
  const contentRoot =
    svgNode.querySelector('[data-lugsail-theme-content="true"]') || svgNode
  const labelColor = theme?.labelColor || theme?.textColor
  const textColor = theme?.textColor || labelColor
  const labelFontScale = Number(theme?.labelFontScale ?? 1)
  const labelOutline = theme?.labelOutline === true
  const labelOutlineColor = getLabelOutlineColor(labelColor)

  Array.from(contentRoot.querySelectorAll('text, tspan, textPath')).forEach((node) => {
    const axisNode = isAxisNode(node)
    // Use explicit paint rather than relying on the SVG-wide CSS text rule.
    // RAWGraphs often supplies fill attributes or inline styles, which win in
    // some renderers and made the Text control appear to affect only axes.
    const nodeColor = axisNode ? textColor : labelColor
    if (nodeColor) {
      node.setAttribute('fill', nodeColor)
      node.style.fill = nodeColor
    }

    if (labelOutline) {
      node.setAttribute('stroke', labelOutlineColor)
      node.style.stroke = labelOutlineColor
      node.setAttribute('stroke-width', '3')
      node.style.strokeWidth = '3px'
      node.setAttribute('paint-order', 'stroke fill')
      node.style.paintOrder = 'stroke fill'
      node.setAttribute('stroke-linejoin', 'round')
      node.style.strokeLinejoin = 'round'
    } else {
      node.removeAttribute('stroke')
      node.removeAttribute('stroke-width')
      node.removeAttribute('paint-order')
      node.removeAttribute('stroke-linejoin')
      node.style.removeProperty('stroke')
      node.style.removeProperty('stroke-width')
      node.style.removeProperty('paint-order')
      node.style.removeProperty('stroke-linejoin')
    }

    if (
      Number.isFinite(labelFontScale) &&
      Math.abs(labelFontScale - 1) > 0.001
    ) {
      // Prefer explicit font-size; fall back to ancestor walk so nodes that
      // inherit size (no explicit attribute) still get scaled. Never use
      // getComputedStyle — it returns 16px on detached nodes.
      const baseFontSizeRaw =
        node.dataset.rawgraphsThemeBaseFontSize ||
        node.getAttribute('font-size') ||
        node.style.fontSize ||
        getInheritedFontSize(node)
      const baseFontSize = Number.parseFloat(baseFontSizeRaw)
      if (Number.isFinite(baseFontSize)) {
        if (!node.dataset.rawgraphsThemeBaseFontSize) {
          node.dataset.rawgraphsThemeBaseFontSize = String(baseFontSizeRaw)
        }
        const nextSize = Math.max(
          1,
          Math.round(baseFontSize * labelFontScale * 10) / 10
        )
        node.setAttribute('font-size', String(nextSize))
        node.style.fontSize = `${nextSize}px`
      }
    }
  })
}

function reflowLegendCells(svgNode, labelFontScale) {
  if (!Number.isFinite(labelFontScale) || Math.abs(labelFontScale - 1) <= 0.001) return

  svgNode
    .querySelectorAll('.legendColor, .legendSize, .legendShape, .legendSymbol')
    .forEach((cellGroup) => {
      Array.from(cellGroup.querySelectorAll('.cell')).forEach((cell) => {
        const transform = cell.getAttribute('transform') || ''
        const match = transform.match(
          /translate\s*\(\s*([^,\s)]+)(?:[,\s]\s*([^)\s]+))?\s*\)/
        )
        const x = match ? parseFloat(match[1]) : 0
        const rawY = match ? parseFloat(match[2] || 0) : 0

        // Cache the original Y so re-application doesn't compound the scale
        if (cell.dataset.rawgraphsThemeCellY === undefined) {
          cell.dataset.rawgraphsThemeCellY = String(rawY)
        }
        const originalY = parseFloat(cell.dataset.rawgraphsThemeCellY)
        cell.setAttribute('transform', `translate(${x},${originalY * labelFontScale})`)
      })
    })
}

function getLabelOutlineColor(labelColor) {
  const normalized = normalizeCssColor(labelColor)
  if (!normalized) {
    return 'rgba(255, 255, 255, 0.92)'
  }

  const r = Number.parseInt(normalized.slice(1, 3), 16)
  const g = Number.parseInt(normalized.slice(3, 5), 16)
  const b = Number.parseInt(normalized.slice(5, 7), 16)
  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255

  return luminance > 0.58
    ? 'rgba(15, 23, 42, 0.78)'
    : 'rgba(255, 255, 255, 0.92)'
}

function unwrapThemeContent(svgNode) {
  const contentGroup = svgNode.querySelector('[data-lugsail-theme-content="true"]')
  if (!contentGroup) return

  while (contentGroup.firstChild) {
    svgNode.insertBefore(contentGroup.firstChild, contentGroup)
  }
  contentGroup.remove()
}

function restoreOriginalViewport(svgNode) {
  const originalHeight = svgNode.dataset.rawgraphsThemeOriginalHeight
  const originalViewBox = svgNode.dataset.rawgraphsThemeOriginalViewBox

  if (originalHeight) {
    svgNode.setAttribute('height', originalHeight)
  }
  if (originalViewBox) {
    svgNode.setAttribute('viewBox', originalViewBox)
  }
}

function clearThemeArtifacts(svgNode) {
  unwrapThemeContent(svgNode)
  svgNode
    .querySelectorAll(
      '[data-lugsail-theme-background="true"], [data-lugsail-theme-header="true"], [data-lugsail-theme-style="true"]'
    )
    .forEach((node) => node.remove())
  restoreOriginalViewport(svgNode)
}

// RAWGraphs charts commonly include their own opaque `#background` rectangle.
// The themed background rect is intentionally added behind the chart for SVG
// export, but that raw rectangle otherwise sits above it and remains white.
// Recolor the source rectangle too so the rendered artboard is one surface.
function applyBrandThemeToRawBackground(svgNode, backgroundColor) {
  svgNode
    .querySelectorAll('rect#background, rect[data-rawgraphs-background="true"]')
    .forEach((node) => {
      node.setAttribute('fill', backgroundColor)
      node.style.fill = backgroundColor
    })
}

// RAWGraphs chart modules sometimes apply font-family as an inline declaration
// on individual text nodes. SVG inheritance and a root-level stylesheet cannot
// override that declaration, which made the Font control appear ineffective.
function applyBrandFontToText(svgNode, fontFamily) {
  svgNode.querySelectorAll('text').forEach((node) => {
    node.style.setProperty('font-family', fontFamily, 'important')
  })
}

export function applyBrandThemeToSvg(
  svgNode,
  theme = DEFAULT_BRAND_THEME,
  metricsOverride = null,
  options = {}
) {
  if (!svgNode) return

  const { recolorMarks = true, colorScaleOption = null, chartId = null } = options

  clearThemeArtifacts(svgNode)

  const {
    secondaryColor,
    backgroundColor,
    textColor,
    labelColor,
    labelFontScale,
    labelOutline,
    fontFamily,
    title,
    subtitle,
  } = { ...DEFAULT_BRAND_THEME, ...theme }

  if (!svgNode.dataset.rawgraphsThemeOriginalHeight && svgNode.getAttribute('height')) {
    svgNode.dataset.rawgraphsThemeOriginalHeight = svgNode.getAttribute('height')
  }
  if (!svgNode.dataset.rawgraphsThemeOriginalViewBox && svgNode.getAttribute('viewBox')) {
    svgNode.dataset.rawgraphsThemeOriginalViewBox = svgNode.getAttribute('viewBox')
  }

  const metrics = metricsOverride || getSvgMetrics(svgNode)
  const hasHeader = Boolean(title || subtitle)

  applyBrandThemeToRawBackground(svgNode, backgroundColor)

  // Scale header typography with chart width so titles stay legible at large canvas sizes.
  // Reference width is 1200px; clamp the scale factor to avoid extremes.
  const headerScale = Math.max(0.7, Math.min(1.8, metrics.width / 1200))
  const titleFontSize = Math.round(26 * headerScale)
  const subtitleFontSize = Math.round(14 * headerScale)
  const titlePadding = title ? Math.round(34 * headerScale) : 0
  const subtitlePadding = subtitle ? Math.round(22 * headerScale) : 0
  const headerGap = hasHeader ? Math.round(18 * headerScale) : 0
  const headerPadding = hasHeader ? titlePadding + subtitlePadding + headerGap : 0
  const totalHeight = metrics.height + headerPadding

  const background = createSvgNode('rect', {
    x: metrics.x,
    y: metrics.y,
    width: metrics.width,
    height: totalHeight,
    fill: backgroundColor,
    'data-lugsail-theme-background': 'true',
  })

  const style = createSvgNode('style', {
    'data-lugsail-theme-style': 'true',
  })
  style.textContent = `
    text {
      font-family: ${fontFamily};
      fill: ${textColor};
    }
  `

  const contentGroup = createSvgNode('g', {
    transform: `translate(0 ${headerPadding})`,
    'data-lugsail-theme-content': 'true',
  })

  const movableChildren = Array.from(svgNode.childNodes)
  movableChildren.forEach((child) => {
    contentGroup.appendChild(child)
  })

  svgNode.appendChild(background)
  svgNode.appendChild(contentGroup)
  svgNode.appendChild(style)

  if (metrics.hasViewBox) {
    svgNode.setAttribute(
      'viewBox',
      `${metrics.x} ${metrics.y} ${metrics.width} ${totalHeight}`
    )
  } else if (totalHeight) {
    svgNode.setAttribute('height', String(totalHeight))
  }

  if (hasHeader) {
    const headerInset = Math.round(24 * headerScale)
    const headerTopOffset = Math.round(28 * headerScale)
    const header = createSvgNode('g', {
      transform: `translate(${metrics.x + headerInset} ${metrics.y + headerTopOffset})`,
      'data-lugsail-theme-header': 'true',
    })

    if (title) {
      const titleNode = createSvgNode('text', {
        x: 0,
        y: 0,
        fill: textColor,
        'font-size': titleFontSize,
        'font-weight': 700,
      })
      titleNode.textContent = title
      header.appendChild(titleNode)
    }

    const subtitleY = title ? Math.round(24 * headerScale) : 0
    if (subtitle) {
      const subtitleNode = createSvgNode('text', {
        x: 0,
        y: subtitleY,
        fill: textColor,
        'font-size': subtitleFontSize,
      })
      subtitleNode.textContent = subtitle
      header.appendChild(subtitleNode)
    }

    const lineY = title
      ? subtitle
        ? subtitleY + Math.round(14 * headerScale)
        : Math.round(18 * headerScale)
      : Math.round(14 * headerScale)
    header.appendChild(
      createSvgNode('line', {
        x1: 0,
        y1: lineY,
        x2: Math.max(metrics.width - headerInset * 2, 0),
        y2: lineY,
        stroke: secondaryColor,
        'stroke-width': Math.max(1, Math.round(2.5 * headerScale)),
      })
    )

    svgNode.appendChild(header)
  }

  if (recolorMarks) {
    applyBrandThemeToMarks(svgNode, theme)
  }
  applyConfiguredColorScaleOpacity(svgNode, colorScaleOption)
  applyBrandThemeOpacityToMarks(svgNode, theme)
  applyBrandThemeToLineStrokeWidth(svgNode, theme, chartId)
  applyBrandThemeToLabels(svgNode, {
    labelColor,
    textColor,
    labelFontScale,
    labelOutline,
  })
  reflowLegendCells(svgNode, labelFontScale ?? 1)
  applyBrandFontToText(svgNode, fontFamily)

  svgNode.style.backgroundColor = backgroundColor
  svgNode.style.fontFamily = fontFamily
}
